import { createI18n } from 'vue-i18n'
import fallbackMessages from '@/locales/it.json'
import pmsMessages from '@/locales/pms.json'
import pmsAgendaOpsMessages from '@/locales/pmsAgendaOps.json'
import pmsCommsMessages from '@/locales/pmsComms.json'
import pmsDriveMessages from '@/locales/pmsDrive.json'
import pmsPartnerMessages from '@/locales/pmsPartner.json'
import pmsPipelineMessages from '@/locales/pmsPipeline.json'
import portalMessages from '@/locales/portal.json'
import apiClient from '@/api/client'

// PMS and portal namespaces are split into their own JSON files so the
// phase-2 `pms-fe` and `portal-fe` agents can edit disjoint files. They
// are spread into the `it` locale here so all `t()` calls resolve from
// a single message tree (vue-i18n composition mode is mandatory per
// frontend/CLAUDE.md). The flat dot-key style of `it.json` and the
// nested style of `pms.json`/`portal.json` co-exist — vue-i18n handles
// both without any extra config.
//
// Full-app-completion split (registered ONCE here by fe-nav, one file
// per Phase-2 agent — agents extend their own file, never this one):
//   pmsPartner.json   → `partnerBulk.*`            (fe-partner)
//   pmsAgendaOps.json → `agendaOps.*`              (fe-agenda)
//   pmsPipeline.json  → `pipeline.*`               (fe-model)
//   pmsComms.json     → `comms.*` `todo.*` `links.*` (fe-comms)
type MessageTree = Record<string, unknown>

function isPlainObject(value: unknown): value is MessageTree {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * Recursively merge locale message trees. Bundles that share a top-level
 * namespace — `pms.json` (`pms.*`) and `pmsDrive.json` (`pms.drive.*`) both
 * nest under `pms` — must be *deep*-merged: a shallow spread (`{ ...a, ...b }`)
 * replaces the whole `pms` object with the last bundle's, silently blanking
 * every `pms.*` key except the winner's. Objects merge; leaves (strings) are
 * overwritten last-wins. Guarded by `i18n-merge.spec.ts`.
 */
function mergeMessages(...sources: MessageTree[]): MessageTree {
  const out: MessageTree = {}
  for (const source of sources) {
    for (const [key, value] of Object.entries(source)) {
      const existing = out[key]
      out[key] =
        isPlainObject(existing) && isPlainObject(value) ? mergeMessages(existing, value) : value
    }
  }
  return out
}

const bundledIt = mergeMessages(
  fallbackMessages,
  pmsMessages,
  pmsPartnerMessages,
  pmsAgendaOpsMessages,
  pmsPipelineMessages,
  pmsCommsMessages,
  pmsDriveMessages,
  portalMessages,
) as typeof fallbackMessages &
  typeof pmsMessages &
  typeof pmsPartnerMessages &
  typeof pmsAgendaOpsMessages &
  typeof pmsPipelineMessages &
  typeof pmsCommsMessages &
  typeof pmsDriveMessages &
  typeof portalMessages

export type MessageSchema = typeof bundledIt

const i18n = createI18n({
  legacy: false,
  locale: 'it',
  fallbackLocale: 'it',
  messages: {
    it: bundledIt,
  },
})

/**
 * Fetch translations from backend and merge into vue-i18n.
 * Falls back silently to bundled locale file if the API is unreachable.
 */
export async function loadRemoteMessages(locale = 'it'): Promise<void> {
  try {
    const { data } = await apiClient.get<Record<string, string>>(`/static-text/locale/${locale}`)
    i18n.global.setLocaleMessage(locale, {
      ...bundledIt,
      ...data,
    })
  } catch {
    // Backend unreachable — keep bundled fallback
    console.warn(`[i18n] Could not load remote messages for "${locale}", using bundled fallback`)
  }
}

export default i18n
